import { useState, useContext, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { UserContext } from "../utils/context";
import { PROJECT_NAME } from "../utils/constants";
import {
    FileText,
    ShieldCheck,
    GraduationCap,
    PenTool,
    LayoutDashboard,
    History,
    LogOut,
    ChevronRight,
    Sparkles,
    Zap,
    Plus,
    MessageSquare,
    Trash2,
    ArrowLeft,
    Search,
    X,
    Maximize2,
    FlaskConical,
    FileSearch
} from "lucide-react";

import { useResearch } from "../hooks/useResearch";
import {
    deleteResearchItem,
    analyzeDocument,
    compareDocuments,
    trackEvent
} from "../api/userApi";

// Modular Components
import ItemDetailView from "../components/ItemDetailView";
import ChatAssistant from "../components/ChatAssistant";
import DocumentProcessor from "../components/DocumentProcessor";
import Modal from "../components/Modal";
import { exportToPDF } from "../utils/pdfExport";

const AGENTS = [
    {
        id: "summarizer",
        title: "Paper Summarizer",
        description: "Get concise, high-quality summaries of any academic paper in seconds.",
        icon: FileText,
        color: "from-blue-500 to-indigo-600",
        shadow: "shadow-blue-500/20"
    },
    {
        id: "defense-paper",
        title: "Defend Your Paper",
        description: "Prepare for tough questions and refine your arguments before submission.",
        icon: ShieldCheck,
        color: "from-emerald-500 to-teal-600",
        shadow: "shadow-emerald-500/20"
    },
    {
        id: "defense-thesis",
        title: "Defend Your Thesis",
        description: "The ultimate preparation assistant for your thesis proposal defense.",
        icon: GraduationCap,
        color: "from-purple-500 to-pink-600",
        shadow: "shadow-purple-500/20"
    },
    {
        id: "related-works",
        title: "Writing Assistant",
        description: "Drafting the related works section? Let AI help you connect the dots.",
        icon: PenTool,
        color: "from-orange-500 to-red-600",
        shadow: "shadow-orange-500/20"
    },
    {
        id: "experiment-designer",
        title: "Experiment Designer",
        description: "Design robust experiments and test your scientific hypotheses.",
        icon: FlaskConical,
        color: "from-amber-500 to-orange-600",
        shadow: "shadow-amber-500/20"
    },
    {
        id: "paper-reviewer",
        title: "Paper Reviewer",
        description: "Get a comprehensive peer-review critique of your research paper.",
        icon: FileSearch,
        color: "from-rose-500 to-red-600",
        shadow: "shadow-rose-500/20"
    }
];

export default function Dashboard() {
    const { user, logout } = useContext(UserContext);
    const { researchId } = useParams();
    const navigate = useNavigate();

    const { researches, isLoading, fetchData, quotas, refreshQuotas } = useResearch(user?.uid);
    const currentResearch = useMemo(() =>
        researches.find(r => r.id === researchId),
        [researches, researchId]);

    const handleLogout = async () => {
        await logout();
        navigate("/");
    };

    return (
        <div className="min-h-screen bg-[#0f172a] text-white selection:bg-indigo-500/30 font-sans overflow-x-hidden">
            {/* Background Glow */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-500/10 blur-[120px] rounded-full"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-500/10 blur-[120px] rounded-full"></div>
            </div>

            {/* Header */}
            <header className="relative z-20 border-b border-white/5 bg-[#0f172a]/80 backdrop-blur-xl px-8 py-5 flex justify-between items-center sticky top-0">
                <div className="flex items-center gap-6">
                    <button
                        onClick={() => navigate("/main")}
                        className="p-2.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-white/50 hover:text-white transition-all transform hover:-translate-x-1"
                        title="Back to Vault"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <div className="h-8 w-px bg-white/10"></div>
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/20">
                            <LayoutDashboard size={20} />
                        </div>
                        <div>
                            <h1 className="text-lg font-black tracking-tight flex items-center gap-2">
                                {currentResearch?.title || "Research"} <span className="text-white/20 uppercase text-[10px] tracking-widest font-black ml-2 px-1.5 py-0.5 border border-white/10 rounded">Project Hub</span>
                            </h1>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-6">
                    <div className="hidden md:flex items-center gap-4 bg-white/5 px-4 py-2 rounded-xl border border-white/10 text-[10px] font-black tracking-widest uppercase">
                        <div className="flex items-center gap-1.5 text-indigo-400">
                            <FileText size={14} /> {quotas.report}
                        </div>
                        <div className="flex items-center gap-1.5 text-blue-400">
                            <MessageSquare size={14} /> {quotas.chat}
                        </div>
                    </div>
                    <div className="h-4 w-px bg-white/10 mx-2 hidden sm:block"></div>
                    <button
                        onClick={handleLogout}
                        className="p-2.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-white/70 hover:text-white transition-all active:scale-95"
                    >
                        <LogOut size={18} />
                    </button>
                </div>
            </header>

            <main className="relative z-10 max-w-7xl mx-auto px-8 py-12">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    {/* Hero */}
                    <header className="mb-20 text-center space-y-4">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-[10px] font-bold uppercase tracking-widest mb-4">
                            <Sparkles size={12} /> Research Hub
                        </div>
                        <h2 className="text-4xl md:text-6xl font-black tracking-tighter bg-clip-text text-transparent bg-gradient-to-b from-white to-white/60">
                            Synthesize & Analyze
                        </h2>
                        <p className="text-white/40 text-lg max-w-2xl mx-auto leading-relaxed">
                            Select a specialized AI expert to deep-dive into your research project.
                        </p>
                    </header>

                    {/* Main Sections - Specialists Only */}
                    <section className="space-y-6">
                        <div className="flex items-center px-2 mb-8">
                            <h3 className="text-xs font-black uppercase tracking-[0.2em] text-white/40 flex items-center gap-2">
                                <Zap size={16} className="text-indigo-500 fill-indigo-500" />
                                Research Specialists
                            </h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {AGENTS.map((agent, index) => (
                                <motion.div
                                    key={agent.id}
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: index * 0.1 }}
                                    onClick={() => {
                                        trackEvent(user.uid, "NAVIGATE_TO_AGENT", { agent_id: agent.id, research_id: researchId });
                                        navigate(`/research/${researchId}/agent/${agent.id}`);
                                    }}
                                    className="group relative cursor-pointer"
                                >
                                    <div className={`absolute inset-0 bg-gradient-to-br ${agent.color} opacity-0 group-hover:opacity-5 blur-3xl transition-opacity rounded-[3rem]`}></div>
                                    <div className="relative h-full bg-white/[0.03] border border-white/10 p-8 rounded-[3rem] backdrop-blur-md transition-all group-hover:border-white/20 group-hover:bg-white/[0.04] shadow-xl">
                                        <div className={`w-14 h-14 bg-gradient-to-br ${agent.color} rounded-2xl mb-6 flex items-center justify-center text-white shadow-lg ${agent.shadow} transition-transform group-hover:rotate-3`}>
                                            <agent.icon size={28} />
                                        </div>
                                        <h3 className="text-2xl font-black mb-3 group-hover:text-white transition-colors tracking-tight">{agent.title}</h3>
                                        <p className="text-white/40 text-sm leading-relaxed mb-10 h-10 line-clamp-2">{agent.description}</p>
                                        <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.15em] text-white/30 group-hover:text-indigo-400 transition-colors">
                                            Open Specialist <ChevronRight size={16} className="group-hover:translate-x-1 transition-all" />
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        {/* Synthesis Feature */}
                        <div className="mt-16 p-10 rounded-[4rem] bg-indigo-600/5 border border-indigo-500/10 relative overflow-hidden group cursor-pointer hover:bg-indigo-600/10 transition-all">
                            <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-8">
                                <div>
                                    <h4 className="text-2xl font-black mb-2 tracking-tight">Literature Review Synthesis</h4>
                                    <p className="text-white/40 text-base max-w-xl">Automate the mapping of related works and find cross-paper connections instantly.</p>
                                </div>
                                <button className="px-10 py-5 bg-indigo-600 hover:bg-indigo-500 rounded-2xl text-xs font-black transition-all shadow-xl shadow-indigo-600/20 active:scale-95 whitespace-nowrap uppercase tracking-widest">
                                    Start Synthesis
                                </button>
                            </div>
                            <Sparkles size={160} className="absolute -bottom-16 -right-16 text-indigo-500/10 group-hover:rotate-12 transition-transform duration-700" />
                        </div>
                    </section>
                </motion.div>
            </main>

            <footer className="py-20 border-t border-white/5 text-center px-8 relative z-10 mt-20">
                <p className="text-[10px] text-white/20 font-bold uppercase tracking-[0.4em]">
                    &copy; {new Date().getFullYear()} {PROJECT_NAME} Intelligence Systems
                </p>
            </footer>
        </div>
    );
}
